import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import { consumeSseStream } from '@/lib/sse'
import type { DashboardSnapshot } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

function authHeaders(): Record<string, string> {
  const token = Cookies.get('token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function extractError(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback
  const p = payload as Record<string, unknown>
  const msg = (p.error as Record<string, unknown>)?.message || p.message
  return typeof msg === 'string' ? msg : fallback
}

export async function getDashboardSnapshot(workspaceId: string): Promise<DashboardSnapshot> {
  const token = Cookies.get('token')
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/dashboard`), {
    headers: authHeaders(),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    throw new Error(extractError(payload, res.statusText || 'Failed to load dashboard'))
  }
  return res.json()
}

// Regenerating is deliberately its own endpoint rather than part of the snapshot
// fetch: it is the only path that may spend a model call, so it happens when the
// user asks for it, never as a side effect of syncing data. Returns the whole
// refreshed snapshot so one round trip updates the screen.
export async function regenerateBrief(workspaceId: string): Promise<DashboardSnapshot> {
  const token = Cookies.get('token')
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/dashboard/brief/regenerate`), {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    throw new Error(extractError(payload, res.statusText || 'Failed to regenerate the brief'))
  }
  return res.json()
}

// Approving from the dashboard resumes the same LangGraph thread the chat would,
// so it hits the same endpoint — `source` is what tells the audit trail which
// surface the decision was made on. The response is the resume SSE stream; the
// dashboard has nowhere to render it, so it is drained and discarded and the
// snapshot is refreshed instead. A 409 means someone (or another tab) already
// handled it, which is a normal outcome, not a failure to hide.
export async function resolveApproval(
  conversationId: string,
  action: 'approved' | 'rejected',
): Promise<void> {
  const token = Cookies.get('token')
  const res = await fetch(buildUrl(`/api/chat/conversations/${conversationId}/approve`), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ action, source: 'dashboard' }),
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    throw new Error(extractError(payload, res.statusText || 'Failed to submit your decision'))
  }

  // Read the stream to completion and throw the content away. Cancelling instead
  // would abort the request, and the graph resume along with it — the approved tool
  // calls have to actually run, we just have nowhere on this screen to show them.
  const reader = res.body?.getReader()
  if (reader) {
    try {
      for (;;) {
        const { done } = await reader.read()
        if (done) break
      }
    } finally {
      reader.releaseLock()
    }
  }
}

// Mirrors chatApi.ts's streamConversation: fetch with Accept: text/event-stream +
// auth header + signal, then hand the response to the shared SSE parser.
//
// Wire format (verified against apps/api/src/features/dashboard/stream-manager.ts):
//   event: snapshot -> data is the DashboardSnapshot object itself (not wrapped)
//   event: error    -> data is { message: string }
export async function streamDashboard(
  workspaceId: string,
  onEvent: (snapshot: DashboardSnapshot) => void,
  onError: (message: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = { Accept: 'text/event-stream', ...authHeaders() }

  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/dashboard/stream`), {
    method: 'GET',
    headers,
    signal,
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    throw new Error(extractError(payload, res.statusText || 'Stream failed'))
  }

  await consumeSseStream(res, (event, data) => {
    if (event === 'snapshot') {
      onEvent(data as DashboardSnapshot)
    } else if (event === 'error') {
      const d = data as { message: string }
      onError(d.message)
    }
  })
}
